import { createSlice } from "@reduxjs/toolkit";

const betSlipSlice = createSlice({
  name: "betSlip",
  initialState: {
    bets: [],
    totalOdds: 0,
    totalStake: 0,
    possibleWin: 0,
    maxWin: 50000, // Maximum win limit
    minOdds: 1.01, // Minimum odds allowed
    maxOdds: 1000, // Maximum odds allowed
    betType: "single", // single, multiple, system
    systemOptions: [], // For system bets like 2/3, 3/4, etc.
    isOpen: false, // Betslip UI state
    errors: [], // Validation errors
    pendingOddsChanges: [], // Track odds changes pending user action
    notifications: [], // System notifications
    suspendedMarkets: [], // Track suspended markets
    closedMarkets: [], // Track closed markets
  },
  reducers: {
    addBet: (state, action) => {
      const { match, odd, market, selectionId } = action.payload;

      // Validate odds value
      const oddValue = parseFloat(odd.value);
      if (
        isNaN(oddValue) ||
        oddValue < state.minOdds ||
        oddValue > state.maxOdds
      ) {
        state.errors.push(`Invalid odds value: ${odd.value}`);
        return;
      }

      // Check trading status before adding bet
      const tradingStatus =
        odd.tradingStatus || market?.tradingStatus || "Open";
      if (tradingStatus === "Closed") {
        state.errors.push(
          `Market "${market?.marketName || "Unknown"}" is closed for betting`
        );
        return;
      }

      if (tradingStatus === "Suspended") {
        state.notifications.push({
          id: Date.now(),
          message: `Market "${
            market?.marketName || "Unknown"
          }" is suspended. Bet added but may not be placeable.`,
          type: "warning",
          timestamp: Date.now(),
        });
      }
      // Check if the bet already exists using selectionId (more reliable)
      const existingBetIndex = state.bets.findIndex(
        (bet) => bet.selectionId === selectionId
      );
      // Check for conflicting bets (same match, same market, different selection)
      const conflictingBetIndex = state.bets.findIndex(
        (bet) =>
          bet.match.id === match.id &&
          bet.market?.marketName === market?.marketName &&
          bet.selectionId !== selectionId
      );

      if (existingBetIndex !== -1) {
        // Bet already exists - no action needed
        return;
      }

      // Create new bet object with enhanced properties
      const newBet = {
        id: `${match.id}_${market?.marketName}_${odd.label}_${Date.now()}`, // Unique bet ID
        selectionId: selectionId,
        match,
        odd: {
          ...odd,
          originalValue: odd.value, // Store original odds when bet was added
          currentValue: odd.value, // Current odds (may change)
          lastKnownValue: odd.value, // Track last known value for comparison
        },
        market,
        tradingStatus: tradingStatus,
        addedAt: Date.now(),
        oddsChanged: false,
        oddsChangeHistory: [], // Track odds changes
        isValid: tradingStatus === "Open", // Bet is valid only if market is open
        canBePlaced: tradingStatus === "Open",
      };

      if (conflictingBetIndex !== -1) {
        // Replace conflicting bet
        state.bets[conflictingBetIndex] = newBet;
      } else {
        // Add new bet
        state.bets.push(newBet);
      }

      // Open betslip when adding first bet
      if (state.bets.length === 1) {
        state.isOpen = true;
      }

      // Update bet type if needed
      if (state.bets.length > 1 && state.betType === "single") {
        state.betType = "multiple";
      }

      // Recalculate total odds
      updateTotalOdds(state);

      // Clear any previous errors
      state.errors = state.errors.filter(
        (error) => !error.includes("Invalid odds value")
      );
    },

    removeBet: (state, action) => {
      const { match, odd, market } = action.payload;

      // Remove specific bet
      state.bets = state.bets.filter(
        (bet) =>
          !(
            bet.match.id === match.id &&
            bet.odd.label === odd.label &&
            bet.market?.marketName === market?.marketName
          )
      );

      // Update bet type if needed
      if (state.bets.length <= 1 && state.betType === "multiple") {
        state.betType = "single";
      }

      // If removing all system bets, clear system options
      if (state.bets.length < 2) {
        state.systemOptions = [];
      }

      // Remove related pending odds changes
      state.pendingOddsChanges = state.pendingOddsChanges.filter(
        (change) =>
          !(
            change.matchId === match.id &&
            change.oddLabel === odd.label &&
            change.marketName === market?.marketName
          )
      );

      // Recalculate total odds
      updateTotalOdds(state);
    },

    // Handle real-time odds changes
    handleOddsChange: (state, action) => {
      const {
        matchId,
        marketName,
        oddLabel,
        newOdds,
        tradingStatus = "Open",
        selectionId,
      } = action.payload;

      // Initial logging
      console.group(`🎯 ODDS CHANGE PROCESSING`);
      console.log(`📊 Incoming Data:`, {
        matchId,
        marketName,
        oddLabel,
        newOdds,
        tradingStatus,
        selectionId,
        timestamp: new Date().toISOString(),
      });

      // Log current betslip state
      console.log(`📋 Current Betslip State:`, {
        totalBets: state.bets.length,
        pendingChanges: state.pendingOddsChanges.length,
        notifications: state.notifications.length,
        betIds: state.bets.map((bet) => ({
          id: bet.id,
          selectionId: bet.selectionId,
          label: bet.odd.label,
          currentValue: bet.odd.currentValue,
        })),
      });

      // Find affected bet using selectionId (most reliable)
      const affectedBet = state.bets.find(
        (bet) => bet.selectionId === selectionId
      );

      if (!affectedBet) {
        console.log(`❌ No affected bet found for selectionId: ${selectionId}`);
        console.log(
          `🔍 Available selections in betslip:`,
          state.bets.map((bet) => ({
            selectionId: bet.selectionId,
            label: bet.odd.label,
            matchId: bet.match.id,
            market: bet.market.marketName,
          }))
        );
        console.groupEnd();
        return;
      }

      console.log(`✅ Found affected bet:`, {
        betId: affectedBet.id,
        selectionId: affectedBet.selectionId,
        currentOdds: affectedBet.odd.currentValue,
        label: affectedBet.odd.label,
        matchName: affectedBet.match.gameName,
        marketName: affectedBet.market.marketName,
        currentTradingStatus: affectedBet.tradingStatus,
      });

      // Store the old odds from current bet state
      const oldOdds = affectedBet.odd.currentValue;
      const oddsChanged = newOdds !== oldOdds;
      const statusChanged = tradingStatus !== affectedBet.tradingStatus;

      console.log(`📈 Change Analysis:`, {
        oldOdds,
        newOdds,
        oddsChanged,
        oldStatus: affectedBet.tradingStatus,
        newStatus: tradingStatus,
        statusChanged,
        oddsDirection: oddsChanged
          ? newOdds > oldOdds
            ? "INCREASED"
            : "DECREASED"
          : "NO_CHANGE",
        changeAmount: oddsChanged
          ? Math.abs(newOdds - oldOdds).toFixed(4)
          : "0.0000",
        changePercentage: oddsChanged
          ? (((newOdds - oldOdds) / oldOdds) * 100).toFixed(2) + "%"
          : "0%",
      });

      // Update trading status
      const previousTradingStatus = affectedBet.tradingStatus;
      affectedBet.tradingStatus = tradingStatus;
      affectedBet.canBePlaced = tradingStatus === "Open";
      affectedBet.isValid = tradingStatus === "Open";

      console.log(`🚦 Trading Status Update:`, {
        previous: previousTradingStatus,
        new: tradingStatus,
        canBePlaced: affectedBet.canBePlaced,
        isValid: affectedBet.isValid,
      });

      if (tradingStatus === "Open" && oddsChanged) {
        console.log(`💰 Processing odds change for open market...`);

        // Mark odds as changed
        affectedBet.oddsChanged = true;
        affectedBet.odd.previousValue = oldOdds;
        affectedBet.odd.newValue = newOdds;

        console.log(`📝 Updated bet odds fields:`, {
          oddsChanged: affectedBet.oddsChanged,
          previousValue: affectedBet.odd.previousValue,
          newValue: affectedBet.odd.newValue,
          currentValue: affectedBet.odd.currentValue, // Still old value at this point
        });

        // Add to odds change history
        const historyEntry = {
          from: oldOdds,
          to: newOdds,
          timestamp: Date.now(),
          status: tradingStatus,
        };

        affectedBet.oddsChangeHistory.push(historyEntry);

        console.log(`📚 Added to odds history:`, {
          historyEntry,
          totalHistoryEntries: affectedBet.oddsChangeHistory.length,
        });

        // Check for existing pending change
        const existingChange = state.pendingOddsChanges.find(
          (change) => change.selectionId === selectionId
        );

        if (!existingChange) {
          console.log(`➕ Creating new pending change...`);

          const newPendingChange = {
            id: Date.now(),
            matchId,
            marketName,
            oddLabel,
            selectionId,
            betId: affectedBet.id,
            oldOdds,
            newOdds,
            timestamp: Date.now(),
            tradingStatus,
          };

          state.pendingOddsChanges.push(newPendingChange);

          console.log(`✅ New pending change created:`, newPendingChange);
        } else {
          console.log(`🔄 Updating existing pending change:`, {
            existingChangeId: existingChange.id,
            previousNewOdds: existingChange.newOdds,
            updatedNewOdds: newOdds,
            originalOldOdds: existingChange.oldOdds,
          });

          // Update existing change with new odds
          existingChange.newOdds = newOdds;
          existingChange.timestamp = Date.now();

          console.log(`✅ Existing pending change updated:`, existingChange);
        }

        // Add notification
        const oddsDirection = newOdds > oldOdds ? "increased" : "decreased";
        const changeAmount = Math.abs(newOdds - oldOdds).toFixed(2);

        const notification = {
          id: Date.now(),
          message: `Odds ${oddsDirection} by ${changeAmount} for ${affectedBet.odd.label} in ${affectedBet.match.gameName}`,
          type: "odds_change",
          timestamp: Date.now(),
          betId: affectedBet.id,
          selectionId,
          oldOdds,
          newOdds,
        };

        state.notifications.push(notification);

        console.log(`🔔 Notification created:`, notification);
      } else if (tradingStatus !== "Open") {
        console.log(`🚫 Processing non-open market status change...`);

        // Handle market status changes
        const wasOddsChanged = affectedBet.oddsChanged;
        affectedBet.oddsChanged = false; // Clear any pending odds changes

        console.log(`🧹 Clearing odds change state:`, {
          wasOddsChanged,
          nowOddsChanged: affectedBet.oddsChanged,
        });

        // Remove any pending odds changes for this selection
        const pendingChangesBefore = state.pendingOddsChanges.length;
        state.pendingOddsChanges = state.pendingOddsChanges.filter(
          (change) => change.selectionId !== selectionId
        );
        const pendingChangesAfter = state.pendingOddsChanges.length;

        console.log(`🗑️ Removed pending changes:`, {
          before: pendingChangesBefore,
          after: pendingChangesAfter,
          removed: pendingChangesBefore - pendingChangesAfter,
        });

        // Add notification about status change
        const statusNotification = {
          id: Date.now(),
          message: `Market "${marketName}" is now ${tradingStatus} for ${affectedBet.match.gameName}`,
          type: "status_change",
          timestamp: Date.now(),
          betId: affectedBet.id,
          selectionId,
        };

        state.notifications.push(statusNotification);

        console.log(
          `🔔 Status change notification created:`,
          statusNotification
        );

        // Add error if market is no longer available
        if (tradingStatus === "Closed") {
          const errorMessage = `Market "${marketName}" is closed and bets cannot be placed`;
          state.errors.push(errorMessage);

          console.log(`❌ Error added for closed market:`, errorMessage);
        }
      } else {
        console.log(`ℹ️ No significant changes to process:`, {
          reason:
            tradingStatus === "Open" ? "Odds unchanged" : "Market not open",
          oddsChanged,
          tradingStatus,
        });
      }

      // Update last known value
      const previousLastKnownValue = affectedBet.odd.lastKnownValue;
      affectedBet.odd.lastKnownValue = newOdds;

      console.log(`💾 Updated last known value:`, {
        previous: previousLastKnownValue,
        new: affectedBet.odd.lastKnownValue,
      });

      // Log final bet state
      console.log(`📊 Final bet state:`, {
        id: affectedBet.id,
        selectionId: affectedBet.selectionId,
        oddsChanged: affectedBet.oddsChanged,
        tradingStatus: affectedBet.tradingStatus,
        canBePlaced: affectedBet.canBePlaced,
        isValid: affectedBet.isValid,
        oddValues: {
          currentValue: affectedBet.odd.currentValue,
          previousValue: affectedBet.odd.previousValue,
          newValue: affectedBet.odd.newValue,
          lastKnownValue: affectedBet.odd.lastKnownValue,
        },
        historyCount: affectedBet.oddsChangeHistory.length,
      });

      // Recalculate odds since a bet was affected
      console.log(`🧮 Recalculating total odds...`);
      const beforeTotalOdds = state.totalOdds;
      const beforePossibleWin = state.possibleWin;

      updateTotalOdds(state);

      console.log(`📈 Odds recalculation complete:`, {
        totalOdds: {
          before: beforeTotalOdds,
          after: state.totalOdds,
          changed: beforeTotalOdds !== state.totalOdds,
        },
        possibleWin: {
          before: beforePossibleWin,
          after: state.possibleWin,
          changed: beforePossibleWin !== state.possibleWin,
        },
      });

      // Final state summary
      console.log(`📋 Final betslip state summary:`, {
        totalBets: state.bets.length,
        pendingChanges: state.pendingOddsChanges.length,
        notifications: state.notifications.length,
        errors: state.errors.length,
        totalOdds: state.totalOdds,
        possibleWin: state.possibleWin,
        betsWithOddsChanges: state.bets.filter((bet) => bet.oddsChanged).length,
        betsValidForPlacement: state.bets.filter((bet) => bet.canBePlaced)
          .length,
      });

      console.groupEnd();
    },

    acceptOddsChange: (state, action) => {
      const { betId, selectionId } = action.payload;

      const bet = state.bets.find(
        (b) => b.id === betId || b.selectionId === selectionId
      );

      if (bet && bet.oddsChanged) {
        // Update to new odds
        bet.odd.currentValue = bet.odd.newValue;
        bet.oddsChanged = false;

        // Clear the pending change
        state.pendingOddsChanges = state.pendingOddsChanges.filter(
          (change) =>
            change.betId !== betId && change.selectionId !== selectionId
        );

        // Add acceptance to history
        const latestChange =
          bet.oddsChangeHistory[bet.oddsChangeHistory.length - 1];
        if (latestChange) {
          latestChange.accepted = true;
          latestChange.acceptedAt = Date.now();
        }

        // Remove related notifications
        state.notifications = state.notifications.filter(
          (notification) =>
            (notification.betId !== betId &&
              notification.selectionId !== selectionId) ||
            notification.type !== "odds_change"
        );

        // Recalculate odds
        updateTotalOdds(state);
      }
    },

    // Reject odds change (remove the bet)
    rejectOddsChange: (state, action) => {
      const { betId, selectionId } = action.payload;

      // Remove the bet entirely
      state.bets = state.bets.filter(
        (bet) => bet.id !== betId && bet.selectionId !== selectionId
      );

      // Remove related pending changes
      state.pendingOddsChanges = state.pendingOddsChanges.filter(
        (change) => change.betId !== betId
      );

      // Remove related notifications
      state.notifications = state.notifications.filter(
        (notification) => notification.betId !== betId
      );

      // Update bet type if needed
      if (state.bets.length <= 1 && state.betType === "multiple") {
        state.betType = "single";
      }

      // Recalculate odds
      updateTotalOdds(state);
    },

    setStake: (state, action) => {
      const stake = parseFloat(action.payload);

      if (isNaN(stake) || stake < 0) {
        state.errors.push("Invalid stake amount");
        return;
      }

      state.totalStake = stake;

      // Calculate possible win based on bet type
      calculatePossibleWin(state);

      // Apply maximum win limit
      if (state.possibleWin > state.maxWin) {
        state.errors.push(`Maximum win limit is ${state.maxWin}`);
        // Adjust stake to match max win
        state.totalStake = state.maxWin / state.totalOdds;
        state.possibleWin = state.maxWin;
      }
    },

    setBetType: (state, action) => {
      const newBetType = action.payload;
      if (["single", "multiple", "system"].includes(newBetType)) {
        state.betType = newBetType;

        // Clear system options if not a system bet
        if (newBetType !== "system") {
          state.systemOptions = [];
        }

        // Recalculate possible win
        calculatePossibleWin(state);
      }
    },

    setSystemOptions: (state, action) => {
      state.systemOptions = action.payload;
      // Set bet type to system when system options are provided
      if (state.systemOptions.length > 0) {
        state.betType = "system";
      }
      // Recalculate possible win
      calculatePossibleWin(state);
    },
    updateMarketStatus: (state, action) => {
      const { matchId, marketName, newStatus, selectionId } = action.payload;

      // Find bets affected by this market status change
      let affectedBets = [];

      if (selectionId) {
        // If selectionId provided, find specific bet
        const bet = state.bets.find((b) => b.selectionId === selectionId);
        if (bet) affectedBets = [bet];
      } else {
        // Otherwise find by match and market (fallback)
        affectedBets = state.bets.filter(
          (bet) =>
            bet.match.id === matchId && bet.market?.marketName === marketName
        );
      }

      if (affectedBets.length > 0) {
        // Update status for all affected bets
        affectedBets.forEach((bet) => {
          const oldStatus = bet.tradingStatus;
          bet.tradingStatus = newStatus;
          bet.canBePlaced = newStatus === "Open";
          bet.isValid = newStatus === "Open";

          // Clear any pending odds changes if market is no longer open
          if (newStatus !== "Open" && bet.oddsChanged) {
            bet.oddsChanged = false;
            state.pendingOddsChanges = state.pendingOddsChanges.filter(
              (change) => change.selectionId !== bet.selectionId
            );
          }

          // Add to market tracking
          if (
            newStatus === "Suspended" &&
            !state.suspendedMarkets.includes(marketName)
          ) {
            state.suspendedMarkets.push(marketName);
          } else if (
            newStatus === "Closed" &&
            !state.closedMarkets.includes(marketName)
          ) {
            state.closedMarkets.push(marketName);
          }

          // Remove from tracking if market reopens
          if (newStatus === "Open") {
            state.suspendedMarkets = state.suspendedMarkets.filter(
              (m) => m !== marketName
            );
            state.closedMarkets = state.closedMarkets.filter(
              (m) => m !== marketName
            );
          }
        });

        // Add notification about status change
        state.notifications.push({
          id: Date.now(),
          message: `Market "${marketName}" is now ${newStatus} for ${affectedBets[0].match.gameName}`,
          type: "status_change",
          timestamp: Date.now(),
          marketName,
          newStatus,
          affectedBetsCount: affectedBets.length,
        });

        // Add error if market is no longer available for betting
        if (newStatus === "Closed") {
          state.errors.push(
            `Market "${marketName}" is closed and bets cannot be placed`
          );
        } else if (newStatus === "Suspended") {
          state.errors.push(
            `Market "${marketName}" is suspended. Betting may be temporarily unavailable`
          );
        }
      }
    },

    // Clear specific notification
    clearNotification: (state, action) => {
      const { notificationId } = action.payload;
      state.notifications = state.notifications.filter(
        (notification) => notification.id !== notificationId
      );
    },

    // Clear all notifications
    clearAllNotifications: (state) => {
      state.notifications = [];
    },

    toggleBetslip: (state) => {
      state.isOpen = !state.isOpen;
    },

    clearBetSlip: (state) => {
      state.bets = [];
      state.totalOdds = 0;
      state.totalStake = 0;
      state.possibleWin = 0;
      state.betType = "single";
      state.systemOptions = [];
      state.errors = [];
      state.pendingOddsChanges = [];
      state.notifications = [];
      state.suspendedMarkets = [];
      state.closedMarkets = [];
    },

    clearErrors: (state) => {
      state.errors = [];
    },

    // Validate betslip before placing bet
    validateBetSlip: (state) => {
      state.errors = []; // Clear existing errors

      if (state.bets.length === 0) {
        state.errors.push("No bets in betslip");
        return;
      }

      if (state.totalStake <= 0) {
        state.errors.push("Please enter a stake amount");
        return;
      }

      if (state.pendingOddsChanges.length > 0) {
        state.errors.push(
          "Please accept or reject all odds changes before placing bet"
        );
        return;
      }

      // Check for invalid bets
      const invalidBets = state.bets.filter(
        (bet) => !bet.isValid || !bet.canBePlaced
      );
      if (invalidBets.length > 0) {
        invalidBets.forEach((bet) => {
          state.errors.push(
            `Cannot place bet on "${bet.odd.label}" - market is ${bet.tradingStatus}`
          );
        });
        return;
      }

      // Check for suspended markets
      const suspendedBets = state.bets.filter(
        (bet) => bet.tradingStatus === "Suspended"
      );
      if (suspendedBets.length > 0) {
        state.errors.push(
          "Some markets are suspended and bets cannot be placed"
        );
        return;
      }
    },
  },
});

// Helper function to update total odds (enhanced)
const updateTotalOdds = (state) => {
  if (state.bets.length === 0) {
    state.totalOdds = 0;
    state.possibleWin = 0;
    return;
  }

  // Only calculate odds for valid bets that can be placed
  const validBets = state.bets.filter((bet) => bet.isValid && bet.canBePlaced);

  if (validBets.length === 0) {
    state.totalOdds = 0;
    state.possibleWin = 0;
    return;
  }

  // Calculate based on bet type
  if (state.betType === "single") {
    // For single bets, we use the highest odds from valid bets
    state.totalOdds = Math.max(
      ...validBets.map((bet) =>
        parseFloat(bet.odd.currentValue || bet.odd.value)
      )
    );
  } else if (state.betType === "multiple") {
    // For multiple bets, we multiply all odds from valid bets
    state.totalOdds = validBets.reduce(
      (acc, bet) => acc * parseFloat(bet.odd.currentValue || bet.odd.value),
      1
    );
  }

  // Calculate possible win
  calculatePossibleWin(state);
};

// Helper function to calculate possible win based on bet type (enhanced)
const calculatePossibleWin = (state) => {
  const stake = state.totalStake;
  const validBets = state.bets.filter((bet) => bet.isValid && bet.canBePlaced);

  if (validBets.length === 0) {
    state.possibleWin = 0;
    return;
  }

  if (state.betType === "single") {
    // For single bets, divide stake evenly among all valid selections
    const stakePerBet = stake / validBets.length;
    state.possibleWin = validBets.reduce(
      (acc, bet) =>
        acc + stakePerBet * parseFloat(bet.odd.currentValue || bet.odd.value),
      0
    );
  } else if (state.betType === "multiple") {
    // For multiples, it's just stake * total odds
    state.possibleWin = stake * state.totalOdds;
  } else if (state.betType === "system") {
    // System bets require complex calculation
    state.possibleWin = calculateSystemBetWin(
      validBets,
      state.systemOptions,
      stake
    );
  }

  // Round to 2 decimal places
  state.possibleWin = Math.round(state.possibleWin * 100) / 100;
};

// Helper function to calculate system bet win (same as before)
const calculateSystemBetWin = (bets, systemOptions, stake) => {
  if (!systemOptions.length || !bets.length) return 0;

  const combinationSize = systemOptions[0];
  const totalCombinations = getCombinationsCount(bets.length, combinationSize);
  const stakePerCombination = stake / totalCombinations;

  const averageOdds =
    bets.reduce(
      (acc, bet) => acc + parseFloat(bet.odd.currentValue || bet.odd.value),
      0
    ) / bets.length;

  return (
    stakePerCombination *
    totalCombinations *
    Math.pow(averageOdds, combinationSize)
  );
};

// Helper function to calculate combinations count (same as before)
const getCombinationsCount = (n, r) => {
  if (r > n) return 0;
  if (r === n || r === 0) return 1;

  let result = 1;
  for (let i = 1; i <= r; i++) {
    result *= n - (r - i);
    result /= i;
  }
  return Math.round(result);
};

export const {
  addBet,
  removeBet,
  handleOddsChange,
  acceptOddsChange,
  rejectOddsChange,
  setStake,
  setBetType,
  setSystemOptions,
  updateMarketStatus,
  clearNotification,
  clearAllNotifications,
  toggleBetslip,
  clearBetSlip,
  clearErrors,
  validateBetSlip,
} = betSlipSlice.actions;

export const betSlipReducer = betSlipSlice.reducer;
